import type { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';

import ConfigService from '@/client/services/api/ConfigService';
import { useAuthStore } from '@/client/services/state/authStore';
import { useConfigStore } from '@/client/services/state/configStore';

// 403 is what continu-api actually answers for a dead token here (`requiresAuthenticated`, via
// the router's gate). 401 is in too because it costs nothing: dropping the session recovers on
// the next sign-in, where an error screen would strand a user whose token had merely lapsed.
const isDeadSession = (error: AxiosError | null) =>
  error?.response?.status === 401 || error?.response?.status === 403;

/**
 * Fetches the signed-in half of the company configuration (hub links, integration links, editor
 * settings, global segmentation ids, the full feature flags) and hands it to the store, which
 * merges it over the public config.
 *
 * `Config` types these fields as required, so the app must not render a signed-in route without
 * them: `useValidateAssignment` would stop enforcing the audience limit and `AudienceLimitsModal`
 * would throw. Hence the two flags this returns rather than a soft fallback.
 */
export const useCompanyConfig = () => {
  const { authConfig, clearSession } = useAuthStore();
  const { config, setCompanyConfig } = useConfigStore();
  const hasSession = !!authConfig?.authtoken && !!config.host;

  const { data, isError, error } = useQuery({
    // Keyed on the user, not the token: a token refresh mid-session is the same tenant's config,
    // and re-keying on it would discard a good response and re-close the render gate below.
    queryKey: ['company-config', config.host, authConfig?.user?._id],
    queryFn: () => ConfigService.getCompanyConfig(),
    enabled: hasSession,
    refetchOnWindowFocus: false,
    // The global default is `retry: false`, which would let one blip decide the whole session.
    retry: (failureCount, err: AxiosError) => !isDeadSession(err) && failureCount < 2,
    onSuccess: setCompanyConfig,
    onError: (err: AxiosError) => {
      // Drop the token rather than navigating: this gate sits above the transition and MCP
      // consent routes, which handle a stale session themselves once they mount.
      if (isDeadSession(err)) {
        setCompanyConfig({});
        clearSession();
        return;
      }
      console.error('Error fetching the company configuration', err.response?.data ?? err);
    },
  });

  return {
    // Gated on having data, not on having settled: a cached error outlives a dead session now
    // that it ends without a page reload, and `isFetched` stays true across one.
    isAwaitingCompanyConfig: hasSession && data === undefined && !isError,
    // `data` is react-query's own "has this ever succeeded", kept across a failed refetch.
    isCompanyConfigUnavailable:
      isError && !isDeadSession(error as AxiosError) && data === undefined,
  };
};
